"use client";
import {useEffect,useRef,useState} from 'react';
import dynamic from 'next/dynamic';
import {ArrowRight,CircleHelp} from 'lucide-react';
import {api} from '@/lib/api';
import {useAuth} from '@/hooks/use-auth';
import {BiteReportSearch} from './bite-report-search';
import {BiteSiteHeader} from './bite-site-header';
import {BiteReportShare} from './bite-report-share';
import {BiteButton,BiteLinkButton,BiteSpinner} from './bite-ui';
import type {ReportResult} from '@/components/report/report-view';
import {BitePublicRefresh} from './bite-public-refresh';
import {useBiteScreen} from './use-bite-screen';
import './bite-shared-report.css';
const BiteReportWorkspace=dynamic(()=>import('./bite-report-workspace').then(module=>module.BiteReportWorkspace),{ssr:false});

export function BiteSharedReport({id,direct=false}:{id:string;direct?:boolean}) {
  const {auth}=useAuth();
  const [jobId,setJobId]=useState(direct?id:'');
  const [report,setReport]=useState<ReportResult|null>(null),[error,setError]=useState(''),[busy,setBusy]=useState(true),[attempt,setAttempt]=useState(0);
  const search=useRef<HTMLDivElement>(null),footerCta=useRef<HTMLDivElement>(null);
  useBiteScreen(footerCta,Boolean(report),'click',`shared-analysis-cta-${id}`,{label:'bite.shared.analyze.view'});
  useEffect(()=>{
    let active=true;setBusy(true);setError('');setReport(null);
    api<{jobId:string;report:ReportResult}>(`/api/ticker/bite/${direct?'reports':'shared'}/${encodeURIComponent(id)}`)
      .then(data=>{if(active){setReport(data.report);setJobId(data.jobId);}})
      .catch(caught=>{if(active)setError(caught instanceof Error?caught.message:'보고서를 불러오지 못했습니다.');})
      .finally(()=>{if(active)setBusy(false);});
    return()=>{active=false;};
  },[id,direct,attempt,auth?.loggedIn]);
  if(report)return <BiteReportWorkspace readingMode="continuous" report={report} reportId={jobId} displayTicker={report.ticker} showQA={false} showAiChartAnalysis={false}
    readerNotice={<BitePublicRefresh jobId={jobId} report={report} onUpdated={setReport}/>}
    toolbar={<div className="bite-report-tools"><div className="bite-report-tools__actions"><BiteLinkButton href="/bite?intro=1" variant="accent" size="sm" data-track="bite.shared.about"><CircleHelp aria-hidden="true"/>바이트란?</BiteLinkButton>{jobId&&<BiteReportShare jobId={jobId}/>}</div></div>}
    mobileHeader={<><div ref={search}><BiteReportSearch/></div>{!report.mijuma&&<header className="bd-report-heading"><h1>{report.companyName||report.ticker} 기업분석 보고서</h1></header>}</>}
    footer={<><div ref={footerCta} className="bite-shared-analysis-cta"><BiteLinkButton href="/bite/home#bite-search" variant="secondary" data-track="bite.shared.analyze" onClick={event=>{
      if(!window.matchMedia('(max-width:767px)').matches)return;
      const input=search.current?.querySelector<HTMLInputElement>('input');
      if(!input)return;
      event.preventDefault();
      input.focus({preventScroll:true});
      search.current?.scrollIntoView({block:'start',behavior:'instant'});
    }}>내 주식, 지금 확인하기<ArrowRight aria-hidden="true"/></BiteLinkButton></div><footer className="bd-footer">본 서비스는 유사투자자문업 신고업체가 불특정 다수에게 제공하는 투자 참고 정보입니다. 투자에는 원금 손실이 발생할 수 있으며, 투자 판단과 책임은 투자자 본인에게 있습니다.</footer></>}/>;
  return <div className="bd-page bd-report-page bite-scope bite-light" data-system="bite" data-bite-theme="light">
    <BiteSiteHeader mode="종목 분석 보고서"/>
    <main className="bd-report">
      <BiteReportSearch/>
      <header className="bd-report-heading"><h1>기업분석 보고서</h1></header>
      {error&&<p role="alert">{error}</p>}
      {error&&<BiteButton onClick={()=>setAttempt(value=>value+1)} disabled={busy}>다시 불러오기</BiteButton>}
      {busy&&<BiteSpinner label="공유 보고서를 불러오고 있습니다"/>}
      <BiteLinkButton href="/" variant="secondary">내 종목 분석하기</BiteLinkButton>
    </main>
    <footer className="bd-footer">본 서비스는 유사투자자문업 신고업체가 불특정 다수에게 제공하는 투자 참고 정보입니다. 투자에는 원금 손실이 발생할 수 있으며, 투자 판단과 책임은 투자자 본인에게 있습니다.</footer>
  </div>;
}
